import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { api } from "../lib/api";
import { Scale, Clock, Plus, Play } from "lucide-react";
import ScaleSimulation from "../components/scale-simulation";
import SessionControlPanel from "../components/session-control-panel";

export default function Sessions() {
  const [isWeighingDialogOpen, setIsWeighingDialogOpen] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null);
  const [activeLane] = useState(1); // Could be made configurable

  const { data: sessions, isLoading } = useQuery({
    queryKey: ["/api/sessions"],
    queryFn: api.getSessions,
  });

  const { data: visits } = useQuery({
    queryKey: ["/api/visits"],
    queryFn: api.getVisits,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-500/20 text-green-400";
      case "closed":
        return "bg-muted text-muted-foreground";
      default:
        return "bg-blue-500/20 text-blue-400";
    }
  };

  return (
    <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[hsl(187,100%,42%)]">Weigh Sessions</h1>
              <p className="text-muted-foreground">Monitor and manage weighbridge sessions</p>
            </div>
            
            <Dialog open={isWeighingDialogOpen} onOpenChange={setIsWeighingDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="button-start-weighing">
                  <Play className="w-4 h-4 mr-2" />
                  Start Weighing
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center">
                    <Scale className="w-5 h-5 mr-2" />
                    Weighbridge Operations - Lane {activeLane}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Session Control */}
                  <div className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>Visit Selection</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Select 
                          value={selectedVisitId?.toString() || ""} 
                          onValueChange={(value) => setSelectedVisitId(Number(value))}
                        >
                          <SelectTrigger data-testid="select-visit">
                            <SelectValue placeholder="Select a visit to weigh" />
                          </SelectTrigger>
                          <SelectContent>
                            {visits?.filter(v => v.status === "open").map((visit) => (
                              <SelectItem key={visit.id} value={visit.id.toString()}>
                                #{visit.id} - {visit.plateNumber || visit.subjectExternalId}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </CardContent>
                    </Card>

                    <SessionControlPanel 
                      visitId={selectedVisitId || undefined}
                      laneId={activeLane}
                      onSessionCreated={(sessionId) => {
                        console.log(`Session ${sessionId} created`);
                      }}
                    />
                  </div>

                  {/* Scale Simulation */}
                  <div>
                    <ScaleSimulation 
                      onReading={(type, weight, stable) => {
                        console.log(`${type} reading: ${weight}kg, stable: ${stable}`);
                      }}
                      currentMode="idle"
                      laneId={activeLane}
                    />
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>All Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-16 bg-gray-100 rounded animate-pulse"></div>
                  ))}
                </div>
              ) : sessions && sessions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Session ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Visit ID</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Lane</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Order</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Weights</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {sessions.map((session) => (
                        <tr key={session.id} className="hover:bg-muted/30">
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              <Scale className="w-4 h-4 mr-2 text-primary" />
                              <span className="text-sm font-medium" data-testid={`session-id-${session.id}`}>
                                #{session.id}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm">#{session.visitId}</td>
                          <td className="px-4 py-4 text-sm">Lane {session.laneId}</td>
                          <td className="px-4 py-4 text-sm">{session.orderExternalId || "-"}</td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(session.status)}`}>
                              {session.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {session.status === "closed" ? (
                              <div>
                                <div>Tare: {session.tareWeight ? `${session.tareWeight}kg` : "-"}</div>
                                <div>Gross: {session.grossWeight ? `${session.grossWeight}kg` : "-"}</div>
                                <div className="font-medium">Net: {session.netWeight ? `${session.netWeight}kg` : "-"}</div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">In progress</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-muted-foreground">
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {new Date(session.createdAt).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No weighing sessions found. Sessions will appear here once vehicles start weighing.
                </div>
              )}
            </CardContent>
          </Card>
    </div>
  );
}
